/**
 * The client half of the official-profile link rule, so a customer learns that
 * `x.com/theirdomain.com` is a dead link while they are looking at the field,
 * not after a save comes back 400.
 *
 * Deliberately a copy of the minimal rule in
 * `server/src/utils/socialProfileUrl.ts`, never an import: the server is the
 * authority and re-runs the full rule (tracking params, every recognized host)
 * on save. This file only has to agree about what is rejected and why.
 */

interface KnownSocialHost {
  label: string
  example: string
  /** When set, the first path segment must be one of these AND be followed by a handle. */
  firstSegments?: string[]
  /** When set, the path must have exactly this many segments. */
  exactSegments?: number
}

const KNOWN_SOCIAL_HOSTS: Record<string, KnownSocialHost> = {
  'linkedin.com': {
    label: 'LinkedIn',
    example: 'https://linkedin.com/company/yourcompany',
    firstSegments: ['in', 'company', 'school', 'showcase'],
  },
  'facebook.com': { label: 'Facebook', example: 'https://facebook.com/yourpage' },
  'instagram.com': { label: 'Instagram', example: 'https://instagram.com/yourhandle' },
  'x.com': { label: 'x.com', example: 'https://x.com/yourhandle' },
  'twitter.com': { label: 'Twitter', example: 'https://twitter.com/yourhandle' },
  'youtube.com': { label: 'YouTube', example: 'https://youtube.com/@yourchannel' },
  'github.com': { label: 'GitHub', example: 'https://github.com/yourorg', exactSegments: 1 },
  'crunchbase.com': {
    label: 'Crunchbase',
    example: 'https://crunchbase.com/organization/yourcompany',
  },
  'g2.com': { label: 'G2', example: 'https://g2.com/products/yourproduct/reviews' },
  'capterra.com': { label: 'Capterra', example: 'https://capterra.com/p/000000/yourproduct' },
  'trustpilot.com': { label: 'Trustpilot', example: 'https://trustpilot.com/review/yourdomain.com' },
  'tiktok.com': { label: 'TikTok', example: 'https://tiktok.com/@yourhandle' },
  'threads.net': { label: 'Threads', example: 'https://threads.net/@yourhandle' },
  'pinterest.com': { label: 'Pinterest', example: 'https://pinterest.com/yourhandle' },
  'substack.com': { label: 'Substack', example: 'https://substack.com/@yourhandle' },
  'medium.com': { label: 'Medium', example: 'https://medium.com/@yourhandle' },
}

/** A path segment that is a domain name where a handle belongs. Always a dead link. */
const BARE_DOMAIN_SEGMENT = /^[a-z0-9-]+\.[a-z]{2,}$/i

const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i

export type SocialProfileUrlResult =
  | { ok: true; normalized: string }
  | { ok: false; message: string }

/**
 * Validate one link the customer typed into the official-profiles field.
 * Forgiving on input (a bare `linkedin.com/company/acme` gets its scheme
 * added), strict about shapes that cannot resolve to a profile. Hosts we do not
 * recognize are accepted as typed.
 */
export function validateSocialProfileUrl(raw: string): SocialProfileUrlResult {
  const trimmed = (raw ?? '').trim()
  if (!trimmed) {
    return { ok: false, message: 'Enter a profile URL.' }
  }

  if (HAS_SCHEME.test(trimmed) && !/^https?:\/\//i.test(trimmed)) {
    return { ok: false, message: 'Profile links must start with http:// or https://' }
  }

  let parsed: URL
  try {
    parsed = new URL(HAS_SCHEME.test(trimmed) ? trimmed : `https://${trimmed}`)
  } catch {
    return { ok: false, message: `${trimmed} is not a valid web address.` }
  }

  if (!parsed.hostname || !parsed.hostname.includes('.')) {
    return { ok: false, message: `${trimmed} is not a valid web address.` }
  }

  const host = parsed.hostname.toLowerCase()
  // Host lowercased, path case preserved: handles are case sensitive on some
  // platforms.
  const normalized = `${parsed.protocol}//${host}${parsed.port ? `:${parsed.port}` : ''}${parsed.pathname.replace(/\/+$/, '')}${parsed.search}${parsed.hash}`

  const known = KNOWN_SOCIAL_HOSTS[host.replace(/^www\./, '')]
  if (!known) {
    return { ok: true, normalized }
  }

  const segments = parsed.pathname.split('/').filter(Boolean)
  const pointAtAProfile = `${known.label} links should point to a profile like ${known.example}`

  if (segments.length === 0) {
    return { ok: false, message: `${pointAtAProfile}.` }
  }

  if (BARE_DOMAIN_SEGMENT.test(segments[0])) {
    return { ok: false, message: `${pointAtAProfile}, not to a domain name.` }
  }

  if (known.exactSegments !== undefined && segments.length !== known.exactSegments) {
    return { ok: false, message: `${pointAtAProfile}.` }
  }

  if (known.firstSegments && (!known.firstSegments.includes(segments[0]) || segments.length < 2)) {
    return { ok: false, message: `${pointAtAProfile}.` }
  }

  return { ok: true, normalized }
}
